import { useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * IMPORTANTE: Este hook debe estar sincronizado con el backend
 * 
 * Backend configurado en: backend/app/core/config.py
 * - ACCESS_TOKEN_EXPIRE_MINUTES: 35
 * 
 * Frontend configurado en: frontend/src/hooks/useInactivityLogout.js  
 * - timeoutMinutes: 35 (debe coincidir con el backend)
 * 
 * Si cambias uno, cambia el otro también.
 */

/**
 * Hook personalizado para manejar el logout automático por inactividad
 * @param {number} timeoutMinutes - Minutos de inactividad antes del logout (default: 35)
 * 
 * IMPORTANTE: Debe coincidir con ACCESS_TOKEN_EXPIRE_MINUTES del backend
 */
const useInactivityLogout = (timeoutMinutes = 35) => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const timeoutId = useRef(null);
  const warningTimeoutId = useRef(null);
  const lastActivityTime = useRef(Date.now());

  // Tiempo en milisegundos
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_TIME_MS = 5 * 60 * 1000; // Advertencia 5 minutos antes del logout

  const resetTimer = () => {
    // Actualizar el tiempo de última actividad
    const now = Date.now();
    const timeSinceLastReset = now - lastActivityTime.current;
    
    // Solo hacer log si ha pasado más de 5 segundos desde el último reset (para no spamear)
    if (timeSinceLastReset > 5000) {
      console.log(`🔄 Activity detected - resetting ${timeoutMinutes} minute timer (JWT expires in ${timeoutMinutes} min)`);
      console.log(`⏱️ Timer reset at: ${new Date().toLocaleTimeString()}`);
    }
    
    lastActivityTime.current = now;
    
    // Limpiar timeouts existentes
    if (timeoutId.current) {
      console.log(`🧹 Clearing existing timeout ID: ${timeoutId.current}`);
      clearTimeout(timeoutId.current);
    }
    if (warningTimeoutId.current) {
      console.log(`🧹 Clearing existing warning timeout ID: ${warningTimeoutId.current}`);
      clearTimeout(warningTimeoutId.current);
    }

    // Only configure timers if the user is authenticated
    if (user) {
      // Configurar advertencia con un modal más elegante
      if (TIMEOUT_MS > WARNING_TIME_MS) {
        warningTimeoutId.current = setTimeout(() => {
          const remainingMinutes = Math.ceil(WARNING_TIME_MS / 60000);
          console.log(`⚠️ Inactivity warning: ${remainingMinutes} minutes remaining before logout`);
          
          // Disparar un evento personalizado para mostrar advertencia
          window.dispatchEvent(new CustomEvent('inactivityWarning', { 
            detail: { remainingMinutes } 
          }));
        }, TIMEOUT_MS - WARNING_TIME_MS);
        console.log(`⏰ Warning timeout set for ${TIMEOUT_MS - WARNING_TIME_MS}ms (ID: ${warningTimeoutId.current})`);
      }

      // Configurar logout automático
      timeoutId.current = setTimeout(() => {
        console.log('⏰ Session expired due to inactivity - logging out user');
        // Usar el nuevo sistema de logout con razón
        logout('inactivity');
      }, TIMEOUT_MS);
      console.log(`⏰ Logout timeout set for ${TIMEOUT_MS}ms (ID: ${timeoutId.current})`);
    }
  };

  useEffect(() => {
    // Events that indicate REAL user activity (not automatic)
    const events = [
      'mousedown',    // Click del mouse
      'keypress',     // Tecla presionada
      'scroll',       // Scroll manual
      'touchstart',   // Inicio de toque en pantalla
      'click',        // Click en elementos
      'keydown',      // Tecla presionada
      'wheel',        // Scroll con rueda del mouse
      'touchend',     // Fin de toque (para móviles)
      'pointerdown',  // Inicio de interacción (mouse, touch, pen)
      // Removidos: mousemove, pointermove, touchmove, focus (pueden ser automáticos)
    ];

    // Función throttle mejorada para no resetear el timer en cada evento
    let throttleTimer = null;
    const handleUserActivity = () => {
      if (!throttleTimer) {
        throttleTimer = setTimeout(() => {
          throttleTimer = null;
          
          // Resetear el timer siempre que haya actividad
          // El throttle de 1 segundo ya evita múltiples resets
          resetTimer();
        }, 1000); // Throttle de 1 segundo para evitar múltiples resets
      }
    };

    // Agregar listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity);
    });

    // Inicializar el timer
    console.log(`🔄 Initializing inactivity timer: ${timeoutMinutes} minutes`);
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity);
      });
      
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
      if (warningTimeoutId.current) {
        clearTimeout(warningTimeoutId.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [user, navigate, logout, TIMEOUT_MS]);

  // Efecto para detectar cambios de ruta (navegación)
  useEffect(() => {
    if (user) {
      console.log('🔄 Route changed, resetting inactivity timer');
      resetTimer();
    }
  }, [location.pathname, user]);

  // Note: We DON'T intercept automatic API calls because they are not user activity
  // Solo las interacciones reales (clicks, navegación, scroll, etc.) deberían contar

  // Function to manually extend the session
  const extendSession = () => {
    console.log('🔄 Manually extending session');
    resetTimer();
  };

  return { extendSession };
};

export default useInactivityLogout;
